import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import SmartDateInput from "@/components/SmartDateInput";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowUpRight, User, MagnifyingGlass, TrashSimple, ListBullets, X, CheckSquare, BookOpen } from "@phosphor-icons/react";
import { useConfirmDialog } from "@/components/ConfirmDialog";
import { exportRegolamentoPdf } from "@/lib/exportRegolamento";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", gender: "M", height_cm: "", date_of_birth: "", password: "" });
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [studio, setStudio] = useState(null);
  const { confirm, dialog } = useConfirmDialog();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } catch (e) {
      toast.error("Errore nel caricamento clienti");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/studio-settings").then(({ data }) => setStudio(data)).catch(() => {});
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/clients", {
        ...form,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
      });
      toast.success("Cliente creato — ora aggiungi piano e rilievi");
      setOpen(false);
      setForm({ email: "", name: "", gender: "M", height_cm: "", date_of_birth: "", password: "" });
      navigate(`/admin/clients/${data.user_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione");
    }
  };

  const deleteOne = async (client) => {
    const ok = await confirm({
      title: "Eliminare il cliente?",
      description: `«${client.name}» e tutti i suoi dati (piani, rilievi, foto) verranno eliminati definitivamente.`,
      confirmLabel: "Elimina cliente",
    });
    if (!ok) return;
    try {
      await api.delete(`/clients/${client.user_id}`);
      toast.success("Cliente eliminato");
      setSelected((s) => { const n = new Set(s); n.delete(client.user_id); return n; });
      load();
    } catch (e) {
      toast.error("Errore eliminazione: " + (e?.response?.data?.detail || e.message || ""));
    }
  };

  const deleteBulk = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const names = clients.filter(c => selected.has(c.user_id)).map(c => c.name).join(", ");
    const ok = await confirm({
      title: `Eliminare ${ids.length} client${ids.length === 1 ? "e" : "i"}?`,
      description: `${names} verranno rimossi definitivamente insieme a tutti i loro dati.`,
      confirmLabel: "Elimina tutti",
    });
    if (!ok) return;
    let okCount = 0, errCount = 0;
    for (const id of ids) {
      try { await api.delete(`/clients/${id}`); okCount++; } catch (e) { errCount++; }
    }
    if (okCount) toast.success(`Eliminati ${okCount} client${okCount === 1 ? "e" : "i"}`);
    if (errCount) toast.error(`${errCount} eliminazione${errCount === 1 ? "" : "i"} non riuscite`);
    setSelected(new Set());
    setSelectMode(false);
    load();
  };

  const toggleSelected = (id) => {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return clients.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q)
    );
  }, [clients, query]);

  const listToShow = showAll ? clients : filtered;
  const isSearching = query.trim().length > 0;
  const showEmptyState = !loading && !isSearching && !showAll;

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-tiny">Amministrazione</div>
          <h1 className="font-serif text-5xl lg:text-6xl tracking-tight mt-2">Clienti</h1>
          <p className="mt-3 text-sm text-[color:var(--text-secondary)] max-w-lg">
            Cerca un cliente per nome o email, oppure mostra l'elenco completo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="admin-download-regolamento-btn"
            variant="outline"
            onClick={() => exportRegolamentoPdf({ studio })}
            className="rounded-full"
            title="Scarica il regolamento generale in PDF"
          >
            <BookOpen size={14} weight="duotone" className="mr-1.5" /> Regolamento (PDF)
          </Button>
          {selectMode && (
            <Button
              data-testid="cancel-select-btn"
              variant="outline"
              onClick={() => { setSelectMode(false); setSelected(new Set()); }}
              className="rounded-full"
            >
              <X size={14} weight="bold" className="mr-1" /> Annulla selezione
            </Button>
          )}
          {!selectMode && clients.length > 0 && (
            <Button
              data-testid="enter-select-mode-btn"
              variant="outline"
              onClick={() => setSelectMode(true)}
              className="rounded-full"
            >
              <CheckSquare size={14} weight="bold" className="mr-1" /> Seleziona
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-client-btn" className="rounded-full h-11 px-6 bg-[color:var(--accent)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white">
                <Plus size={16} weight="bold" className="mr-2" /> Nuovo cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[color:var(--bg-primary)]">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">Nuovo cliente</DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div>
                  <Label className="label-tiny">Email</Label>
                  <Input data-testid="new-client-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="cliente@email.it" />
                  <p className="text-xs mt-1 text-[color:var(--text-secondary)]">Il cliente accederà con questa email.</p>
                </div>
                <div>
                  <Label className="label-tiny">Password (opzionale)</Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="new-client-password"
                      type="text"
                      value={form.password || ""}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="Lascia vuoto se accederà con Google"
                    />
                    <button
                      type="button"
                      data-testid="new-client-genpwd"
                      onClick={() => {
                        const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
                        let p = "";
                        for (let i = 0; i < 10; i++) p += chars[Math.floor(Math.random() * chars.length)];
                        setForm({ ...form, password: p });
                        navigator.clipboard?.writeText(p).catch(() => {});
                      }}
                      className="rounded-full border border-[color:var(--border)] px-3 h-10 text-xs hover:border-[color:var(--text-primary)] whitespace-nowrap"
                    >
                      Genera
                    </button>
                  </div>
                  <p className="text-[11px] mt-1 text-[color:var(--text-secondary)]">Comunica queste credenziali al cliente. Puoi cambiarle in qualsiasi momento dal profilo.</p>
                </div>
                <div>
                  <Label className="label-tiny">Nome</Label>
                  <Input data-testid="new-client-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mario Rossi" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-tiny">Genere</Label>
                    <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                      <SelectTrigger data-testid="new-client-gender"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Maschile</SelectItem>
                        <SelectItem value="F">Femminile</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-tiny">Altezza (cm)</Label>
                    <Input type="number" value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label className="label-tiny">Data di nascita</Label>
                  <SmartDateInput
                    data-testid="new-client-dob"
                    value={form.date_of_birth}
                    onChange={(iso) => setForm({ ...form, date_of_birth: iso })}
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="create-client-submit" className="rounded-full bg-[color:var(--text-primary)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white">Crea cliente</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search + Show all row */}
      <div className="mt-8 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-xl">
          <MagnifyingGlass size={16} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--text-secondary)]" />
          <Input
            data-testid="search-clients-input"
            value={query}
            onChange={(e) => { setQuery(e.target.value); if (e.target.value) setShowAll(false); }}
            placeholder="Cerca per nome o email…"
            className="pl-11 h-12 rounded-full bg-[color:var(--bg-primary)]"
          />
          {query && (
            <button
              type="button"
              data-testid="clear-search-btn"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"
            >
              <X size={14} weight="bold" />
            </button>
          )}
        </div>
        <Button
          data-testid="toggle-show-all-btn"
          variant={showAll ? "default" : "outline"}
          onClick={() => { setShowAll((v) => !v); setQuery(""); }}
          className={`rounded-full h-12 px-6 ${showAll ? "bg-[color:var(--text-primary)] text-white hover:bg-[color:var(--text-primary)] hover:text-white" : ""}`}
        >
          <ListBullets size={16} weight="bold" className="mr-2" />
          {showAll ? "Nascondi elenco" : `Mostra tutti (${clients.length})`}
        </Button>
      </div>

      {/* Bulk action bar */}
      {selectMode && selected.size > 0 && (
        <div data-testid="bulk-action-bar" className="mt-5 flex items-center justify-between rounded-2xl border border-[color:var(--border)] bg-[color:var(--text-primary)] text-[color:var(--bg-primary)] px-5 py-3">
          <div className="text-sm">
            <span className="font-medium">{selected.size}</span> client{selected.size === 1 ? "e" : "i"} selezionat{selected.size === 1 ? "o" : "i"}
          </div>
          <Button
            data-testid="bulk-delete-btn"
            onClick={deleteBulk}
            className="rounded-full bg-red-700 text-white hover:bg-red-800"
          >
            <TrashSimple size={14} weight="bold" className="mr-1.5" />
            Elimina selezionati
          </Button>
        </div>
      )}

      {/* Empty state */}
      {showEmptyState && (
        <div className="mt-10 border border-dashed border-[color:var(--border)] rounded-2xl p-10 text-center">
          {clients.length === 0 ? (
            <>
              <User size={32} weight="duotone" className="mx-auto text-[color:var(--accent)]" />
              <p className="mt-3 text-sm text-[color:var(--text-secondary)]">Nessun cliente ancora. Crea il primo.</p>
            </>
          ) : (
            <>
              <MagnifyingGlass size={28} weight="duotone" className="mx-auto text-[color:var(--accent)]" />
              <p className="mt-3 text-sm text-[color:var(--text-secondary)]">
                Cerca un cliente per nome o email, oppure mostra l'elenco completo.
              </p>
              <div className="text-xs text-[color:var(--text-secondary)] mt-1">
                {clients.length} client{clients.length === 1 ? "e" : "i"} registrat{clients.length === 1 ? "o" : "i"}.
              </div>
            </>
          )}
        </div>
      )}

      {/* Search / list results */}
      {(isSearching || showAll) && (
        <div className="mt-6">
          {isSearching && (
            <div className="label-tiny mb-4">
              {filtered.length} risultat{filtered.length === 1 ? "o" : "i"} per «{query}»
            </div>
          )}
          {loading && <div className="text-sm">Caricamento…</div>}
          {!loading && listToShow.length === 0 && isSearching && (
            <div className="text-sm text-[color:var(--text-secondary)]">Nessun cliente corrisponde alla ricerca.</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 stagger" data-testid="clients-grid">
            {listToShow.map((c) => {
              const checked = selected.has(c.user_id);
              const goTo = () => { if (!selectMode) navigate(`/admin/clients/${c.user_id}`); };
              return (
                <div
                  key={c.user_id}
                  data-testid={`client-card-${c.user_id}`}
                  onClick={selectMode ? () => toggleSelected(c.user_id) : goTo}
                  className="group border rounded-2xl p-6 transition-transform bg-[color:var(--bg-primary)] cursor-pointer"
                  style={{
                    borderColor: checked ? "var(--accent)" : "var(--border)",
                    transform: "translateY(0)",
                    transition: "transform 0.2s ease, border-color 0.2s ease",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-4px)"}
                  onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-3">
                      {selectMode && (
                        <div className="pt-1">
                          <Checkbox
                            data-testid={`select-client-${c.user_id}`}
                            checked={checked}
                            onCheckedChange={() => toggleSelected(c.user_id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      )}
                      <div>
                        <div className="label-tiny">{c.gender === "F" ? "Femminile" : c.gender === "M" ? "Maschile" : "—"}</div>
                        <div className="font-serif text-2xl mt-1 tracking-tight">{c.name}</div>
                        <div className="text-xs text-[color:var(--text-secondary)] mt-0.5">{c.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {!selectMode && (
                        <>
                          <button
                            type="button"
                            data-testid={`delete-client-${c.user_id}`}
                            onClick={(e) => { e.stopPropagation(); deleteOne(c); }}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-full text-red-700 hover:bg-red-50 border border-transparent hover:border-red-200 transition-colors"
                            title="Elimina cliente"
                          >
                            <TrashSimple size={15} weight="bold" />
                          </button>
                          <ArrowUpRight size={22} weight="duotone" className="text-[color:var(--accent)] opacity-60 group-hover:opacity-100 transition-opacity" />
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-6 flex gap-6 text-xs">
                    <div>
                      <div className="label-tiny">Altezza</div>
                      <div className="mt-1">{c.height_cm ? `${c.height_cm} cm` : "—"}</div>
                    </div>
                    <div>
                      <div className="label-tiny">Nato/a</div>
                      <div className="mt-1">{c.date_of_birth || "—"}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {dialog}
    </div>
  );
}
